import { PayloadAction } from '@reduxjs/toolkit';
import { AuthSelectors, FirebaseActions, LanguageSelectors, waitForFirebaseReadySaga } from '@web_opxp/features';
import find from 'lodash/find';
import { all, call, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import { ApiService } from 'web_core_library';
import { getCheckoutBackUrl, redirectToCheckout, redirectToSSO } from '../base/historyService';
import { CouponActions, CouponSelectors } from '../coupon';
import * as CustomTrackingActions from './customTrackingActions';
import * as Selectors from './selectors';
import { SubscribeActions } from './slice';
import SubscribeService from './subscribeService';
import { IProductData, TProductApiResponse } from './types';
import {
  getProductName,
  getProductPlanId,
  getProductsConfig,
  getProductsVariantConfig,
  isProductDiscounted,
} from './utils';

export function* startSubscribePage() {
  // Init subscribe feature
  yield call(SubscribeService.init, ApiService);
  // wait for firebase feature
  yield call(waitForFirebaseReadySaga);
  // wait for coupon feature
  const couponReady = yield select(CouponSelectors.isLoaded);
  if (!couponReady) {
    yield take(CouponActions.couponUpdate);
  }
  // track page open event
  yield put(CustomTrackingActions.startPageAction());
  // track page open on taboola
  yield put(CustomTrackingActions.trackPageOnTaboola());
  yield put(CustomTrackingActions.trackPageOnOutbrain());

  // get coupon
  const coupon = yield select(CouponSelectors.getCurrentCoupon);
  // load products with coupon if present
  yield put(SubscribeActions.productsRequest(coupon));
}

export function* selectProductSaga(action: PayloadAction<number>) {
  const productId = action.payload;
  yield fork(openCheckoutSaga, productId);
}

export function* openCheckoutSaga(productId: number) {
  const products = yield select(Selectors.getProducts);
  const selectedProduct: IProductData | undefined = find(products, ['id', productId]);
  if (!selectedProduct) {
    throw new Error('Selected product not found!');
  }
  // track open checkout event
  const productName = getProductName(selectedProduct);
  yield put(CustomTrackingActions.redirectToCheckoutAction(productName));
  // get available coupon
  const availableCoupon = yield select(CouponSelectors.getCurrentCoupon);
  // check if selected product is discounted with coupon
  const activeCoupon = isProductDiscounted(selectedProduct) ? availableCoupon : null;
  if (!activeCoupon) {
    // remove coupon from state explicitly see BW-7436 bug
    yield put(CouponActions.couponReset());
  }

  // get language
  const language = yield select(LanguageSelectors.getLanguage);

  // redirect to SSO or directly to checkout checkout page
  const isLoggedIn = yield select(AuthSelectors.isLoggedIn);
  const userVerified = yield select(AuthSelectors.isVerified);

  const planId = getProductPlanId(selectedProduct);
  if (!isLoggedIn || !userVerified) {
    const checkoutUrl = getCheckoutBackUrl(planId, productId, language, selectedProduct.country, activeCoupon);
    yield call(redirectToSSO, checkoutUrl);
    return;
  }
  yield call(redirectToCheckout, planId, productId, language, selectedProduct.country, activeCoupon);
}

export function* requestProductsSaga(action: PayloadAction<string | undefined>) {
  const coupon = action.payload;
  try {
    let products: IProductData[];
    // try to get product ids from firebase config
    const productIds: number[] | undefined = yield call(getProductsConfig);
    if (!productIds?.length) {
      // get full products list from API if failed
      products = yield call(fetchDefaultProducts, coupon);
    } else {
      products = yield call(fetchProductsByIds, productIds, coupon);
    }
    yield put(SubscribeActions.updateProducts(products));
    yield put(SubscribeActions.productsRequestSuccess());
    const variant = yield call(getProductsVariantConfig);
    // set user property
    yield put(FirebaseActions.setUserPropertyAction('experiment_4454', variant));
  } catch (error) {
    yield put(SubscribeActions.productsRequestFail((error as Error).message));
    throw error;
  }
}

export function* fetchDefaultProducts(coupon?: string) {
  // get full products list from API
  const productsResponse: TProductApiResponse = yield call(SubscribeService.getProducts, coupon);
  return productsResponse.data.products || [];
}

export function* fetchProductsByIds(ids: number[], coupon?: string) {
  try {
    // get products definitions for given product ids
    const productsResponse: TProductApiResponse = yield call(SubscribeService.getProductsByIds, ids, coupon);
    return productsResponse.data.products || [];
  } catch (error) {
    return yield call(fetchDefaultProducts, coupon);
  }
}

export default function* watchSubscribe() {
  yield all([
    takeEvery(SubscribeActions.subscribeInit, startSubscribePage),
    takeEvery(SubscribeActions.selectProduct, selectProductSaga),
    takeEvery(SubscribeActions.productsRequest, requestProductsSaga),
  ]);
}
